<template>
	<div class="input-wrapper">
		<div :class="classes" @click="focusOnInputEl">
			<div class="input-col">
				<label class="input-label" :for="id">
					{{ label ?? placeholder }}
				</label>
				<input
					:id
					:min
					:max
					:name
					:disabled
					:required
					:placeholder
					type="number"
					ref="inputEl"
					class="input"
					v-model.number="model"
					@change="emits('change', $event)"
					@input="emits('input', $event)"
				/>
			</div>

			<div v-if="hint" class="input-hint">
				<slot name="hint">{{ hint }}</slot>
			</div>
		</div>

		<p v-if="error && !disabled" class="input-error">{{ error }}</p>
	</div>
</template>

<script setup lang="ts">
import { getRandomId } from '@/utils/index';

const INPUT_SIZES = ['lg', 'xl'] as const;
type ShInputSizesT = 'lg' | 'xl';
type ShInputSizeName = `size-${(typeof INPUT_SIZES)[number]}`;
type ShInputSizeNameAndValue = `size-${(typeof INPUT_SIZES)[number]}${number}`;
type UISizeBckPoint = ShInputSizeName | ShInputSizeNameAndValue;

export type ShInputText = {
	name?: string;
	label?: string;
	disabled?: boolean;
	required?: boolean;
	placeholder?: string;
	error?: string;
	hint?: string;
	min?: number;
	max?: number;
} & { [s in ShInputSizesT]?: number | boolean };

const props = defineProps<ShInputText>();

const model = defineModel<number | string | undefined>({
	required: true,
	default: ''
});
const emits = defineEmits(['blur', 'change', 'input', 'focus']);

const id = ref('');
const inputEl = useTemplateRef('inputEl');

const classes = computed(() => {
	const sizeClasses = getSizeClass();

	const hasValue = model.value !== '';

	return {
		'input-row': true,
		required: props.required,
		filled: hasValue,
		invalid: props.error?.length,
		disabled: props.disabled,
		...sizeClasses
	};
});

onMounted(() => {
	id.value = getRandomId();
});

function focusOnInputEl() {
	inputEl.value?.focus();
}

function getSizeClass() {
	const classes: { [key in UISizeBckPoint]?: boolean } = {};

	INPUT_SIZES.forEach((s) => {
		const value = props[s];

		if (!(s in props)) return;
		if (value === undefined) return;

		if (typeof value === 'boolean') {
			return (classes[`size-${s}`] = value);
		}

		classes[`size-${s}${value}`] = true;
	});

	return classes;
}
</script>

<style scoped lang="scss"></style>
